import { useEffect, useRef, useState } from "react";

import { filter } from "lodash/fp";
import { DateTime } from "luxon";

import { LinearProgress, Slide, Typography } from "@mui/material";

import useCompleteGettingStarted from "@app/analytics/GettingStarted/useCompleteGettingStarted";
import useGettingStarted from "@app/analytics/GettingStarted/useGettingStarted";
import confettiCanon from "@app/common/animations/confettiCanon";
import useAuth from "@app/common/auth/useAuth";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import * as UserUtils from "@app/common/utils/userUtils";
import Config from "@app/config";
import logger from "@app/utils/logger";

import Button from "@rui/Button";
import { ModalCloseIconButton } from "@rui/Modal";
import { Column, Row } from "@rui/Stack";

import * as Events from "./events";
import { ChecklistBox, ChecklistItem, OpenButton, Title } from "./styles";

const RELEASE_DATE = DateTime.fromFormat(
  Config.releaseDates.onboardingChecklist,
  "MM/dd/yyyy"
);

const isReleased = () => {
  return DateTime.now() > RELEASE_DATE;
};

interface GettingStartedProps {
  sideNavOpen: boolean;
  onDismiss: () => void;
}

const GettingStarted = ({ sideNavOpen, onDismiss }: GettingStartedProps) => {
  const { user } = useAuth();
  const left = sideNavOpen ? "260px" : "84px";

  const [open, setOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const confettiRetries = useRef(0);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { progressItems, itemsPendingCompletion } = useGettingStarted();
  const completeGettingStarted = useCompleteGettingStarted();
  const completedProgressItems = filter("completed", progressItems);
  const totalItems = progressItems.length;
  const numCompletedItems = completedProgressItems.length;
  const progressValue = (numCompletedItems / totalItems) * 100;
  const isCompleted = numCompletedItems === totalItems;

  const fireConfetti = () => {
    const targetEl =
      open && containerRef.current ? containerRef.current : buttonRef.current;

    if (targetEl) {
      const clientX =
        targetEl.getBoundingClientRect().left + targetEl.offsetWidth / 2;
      const clientY = targetEl.getBoundingClientRect().top;

      const x = clientX / window.innerWidth;
      const y = clientY / window.innerHeight;

      confettiRetries.current = 0;

      confettiCanon({ origin: { x, y } });
    } else {
      if (confettiRetries.current < 5) {
        // if there isn't a target element, try again in 200ms
        setTimeout(fireConfetti, 200);
        confettiRetries.current++;
        return;
      }
      logger.debug("No target element to fire confetti");
    }
  };

  useEffect(() => {
    if (itemsPendingCompletion.length === 0) return;

    completeGettingStarted.mutate(itemsPendingCompletion, {
      onSuccess: () => fireConfetti(),
    });
  }, [itemsPendingCompletion]);

  const handleOpen = () => {
    Events.openChecklist({ numComplete: numCompletedItems });
    setOpen(true);
  };

  const handleDismiss = () => {
    Events.dismissChecklist({ complete: isCompleted });
    setIsDismissed(true);
  };

  const progressString = `(${numCompletedItems}/${totalItems})`;

  return (
    <>
      <Slide
        direction="up"
        in={open && !isDismissed}
        mountOnEnter
        unmountOnExit
      >
        <ChecklistBox sx={{ left, bottom: "16px" }} ref={containerRef}>
          <Column>
            <Row padding="8px 8px 12px 16px" variant="apart">
              <Title variant="bodyPrimaryMed">
                {isCompleted ? "🏆" : "👋"} Getting Started{" "}
                <span>{progressString}</span>
              </Title>
              <ModalCloseIconButton onClick={() => setOpen(false)} />
            </Row>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{ backgroundColor: "shades.neutral.100" }}
            />
            <Column padding="12px 16px" spacing="12px">
              {isCompleted ? (
                <Typography variant="labelReg" color="shades.neutral.600">
                  Congratulations on getting your account all set up
                  {user?.firstName ? `, ${user.firstName}` : ""}! 🎉 You're now
                  ready to make the most of Read.
                </Typography>
              ) : (
                <Typography variant="labelReg" color="shades.neutral.600">
                  Hey {user?.firstName ?? "there"}, we recommend taking the
                  following steps to make sure you get the most out of Read:
                </Typography>
              )}
              <Column>
                {progressItems.map((item) => (
                  <ChecklistItem
                    key={item.type}
                    label={item.description}
                    checked={item.completed}
                    to={item.pathname}
                  />
                ))}
              </Column>
            </Column>
            <Button
              variant="text"
              color="secondary"
              sx={{ fontWeight: "400", fontSize: "0.75rem", height: "28px" }}
              onClick={handleDismiss}
            >
              {isCompleted ? "Dismiss" : "Don't show this again"}
            </Button>
          </Column>
        </ChecklistBox>
      </Slide>
      <Slide direction="up" in={!isDismissed} onExited={() => onDismiss()}>
        <OpenButton
          sx={{ left }}
          aria-label="getting started"
          onClick={handleOpen}
          ref={buttonRef}
        >
          {isCompleted ? "🏆" : "👋"} Getting Started{" "}
          <span>{progressString}</span>
        </OpenButton>
      </Slide>
    </>
  );
};

const GettingStartedGate = (props: Omit<GettingStartedProps, "onDismiss">) => {
  const { user } = useAuth();

  const [isDismissed, setIsDismissed] = useLocalStorage(
    "gettingStartedDismissed",
    false
  );

  if (isDismissed || !isReleased()) {
    return null;
  }

  // only show to users who were created after this was released
  if (UserUtils.createdBefore(user, RELEASE_DATE)) {
    return null;
  }

  if (!UserUtils.isRecentUser(user)) {
    return null;
  }

  return <GettingStarted {...props} onDismiss={() => setIsDismissed(true)} />;
};

export default GettingStartedGate;
